import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

type ListRowProps = {
  title: ReactNode;
  meta?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  href?: string;
  divider?: boolean;
  emphasis?: 'normal' | 'strong';
  sx?: SxProps<Theme>;
  'aria-current'?: 'page' | undefined;
};

/** Shared `[leading][title+meta][trailing]` row. Interactive only when onClick/href set. */
export const ListRow = ({
  title,
  meta,
  leading,
  trailing,
  onClick,
  href,
  divider = false,
  emphasis = 'normal',
  sx,
  'aria-current': ariaCurrent,
}: ListRowProps) => {
  const interactive = onClick != null || href != null;
  let component: 'a' | 'button' | 'div';
  if (href != null) {
    component = 'a';
  } else if (onClick != null) {
    component = 'button';
  } else {
    component = 'div';
  }

  return (
    <Box
      component={component}
      type={component === 'button' ? 'button' : undefined}
      href={href}
      onClick={onClick}
      aria-current={ariaCurrent}
      sx={[
        theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          textAlign: 'left',
          py: 1.25,
          px: 0.5,
          border: 0,
          background: 'transparent',
          font: 'inherit',
          color: 'inherit',
          textDecoration: 'none',
          ...(divider && {
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
          ...(interactive && {
            minHeight: 44,
            cursor: 'pointer',
            borderRadius: 1,
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }),
        }),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- SxProps array spread is safe; MUI's type lacks the index signature that would allow it
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {leading}
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontWeight: emphasis === 'strong' ? 700 : 600, fontSize: 14.5 }}
          noWrap
        >
          {title}
        </Typography>
        {meta != null && (
          <Typography variant="body2" color="text.secondary">
            {meta}
          </Typography>
        )}
      </Stack>
      {trailing != null && (
        <Box
          sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {trailing}
        </Box>
      )}
    </Box>
  );
};
